import { logger } from '../lib/logger';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { Crown, Globe, Briefcase, Wallet, Droplet, CalendarCheck2, RefreshCcw, Sparkles } from 'lucide-react';

/**
 * HCFProfileCard — Pre-Profilazione cliente (Phase 0).
 *
 * Mostra la "Scheda Cliente HCF™" generata automaticamente dopo il Form 2.
 * Combinazione delle 5 sezioni del manuale operativo:
 *   1. Paese scelto       → leva + paura
 *   2. Interesse          → mentalità + linguaggio
 *   3. Budget             → fascia + attenzione
 *   4. Liquidità (ipotesi) → strategia
 *   5. Prenotazione call  → temperatura + strategia
 *
 * Solo admin/amministrativo possono ricalcolare (re-dispatch DM+email).
 */

const ARCHETYPE_COLORS = {
  VISION: 'bg-violet-100 text-violet-800 border-violet-200',
  SAFE:   'bg-blue-100 text-blue-800 border-blue-200',
  LIFE:   'bg-amber-100 text-amber-800 border-amber-200',
  TIME:   'bg-orange-100 text-orange-800 border-orange-200',
  CASH:   'bg-emerald-100 text-emerald-800 border-emerald-200',
  FAME:   'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
  HOME:   'bg-rose-100 text-rose-800 border-rose-200',
  DEEP:   'bg-slate-100 text-slate-800 border-slate-200',
};

const SIGNAL_COLORS = {
  caldo:   'bg-red-100 text-red-700 border-red-200',
  tiepido: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  freddo:  'bg-blue-100 text-blue-700 border-blue-200',
};

/**
 * Render a safe subset of HTML (only <b>/<strong> and <i>/<em>).
 * This is used to render `profile.summary` which is generated by our own
 * backend (`lead_profiler._build_summary`) — never user input — but we
 * still parse it via React elements (no raw-HTML injection APIs) to
 * eliminate any XSS attack surface from future code paths.
 */
function renderSafeRichText(input) {
  if (typeof input !== 'string' || !input) return null;
  // Tokenize on <b>, </b>, <strong>, </strong>, <i>, </i>, <em>, </em>
  const tokens = input.split(/(<\/?(?:b|i|strong|em)>)/i);
  const out = [];
  let bold = 0;
  let italic = 0;
  tokens.forEach((tok, idx) => {
    if (!tok) return;
    const lower = tok.toLowerCase();
    if (lower === '<b>' || lower === '<strong>') { bold++; return; }
    if (lower === '</b>' || lower === '</strong>') { bold = Math.max(0, bold - 1); return; }
    if (lower === '<i>' || lower === '<em>') { italic++; return; }
    if (lower === '</i>' || lower === '</em>') { italic = Math.max(0, italic - 1); return; }
    // Plain text segment — render with current styles. React escapes by default.
    let node = tok;
    if (italic > 0) node = <em key={`em-${idx}`}>{node}</em>;
    if (bold > 0)   node = <strong key={`b-${idx}`}>{node}</strong>;
    out.push(<React.Fragment key={idx}>{node}</React.Fragment>);
  });
  return out;
}

function ArchetypePill({ code, name, primary }) {
  if (!code) return null;
  const cls = ARCHETYPE_COLORS[code] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold ${cls}`}
      data-testid={`hcf-archetype-${primary ? 'primary' : 'secondary'}`}
      title={name || code}
    >
      {primary && <Crown className="w-3 h-3" />}
      <span className="font-mono">{code}</span>
      {name && <span className="hidden md:inline font-normal opacity-80">· {name}</span>}
    </span>
  );
}

function SectionRow({ icon: Icon, label, value, hint, accent }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-sm font-medium text-foreground">{value || '—'}</div>
        {hint && <div className={`text-xs mt-0.5 ${accent || 'text-muted-foreground'}`}>{hint}</div>}
      </div>
    </div>
  );
}

export function HCFProfileCard({ leadId, isAdmin = false }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [computedInline, setComputedInline] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/leads/${leadId}/hcf-profile`);
      setProfile(r.data?.profile || null);
      setComputedInline(!!r.data?.computed_inline);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        setError(t('hcf.noAccess', 'Non hai accesso a questo profilo.'));
      } else {
        setError(t('hcf.loadError', 'Impossibile caricare il profilo HCF.'));
      }
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (leadId) load();
  }, [leadId, load]);

  const recompute = async () => {
    setRecomputing(true);
    try {
      const r = await api.post(`/leads/${leadId}/hcf-profile/recompute`);
      setProfile(r.data?.profile || null);
      setComputedInline(false);
    } catch (err) {
      logger.debug('HCFProfileCard recompute failed (stale data shown)', err);
    } finally {
      setRecomputing(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="hcf-profile-card-loading">
        <CardContent className="p-4 space-y-3">
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="hcf-profile-card-error">
        <CardContent className="p-4 text-sm text-muted-foreground">{error}</CardContent>
      </Card>
    );
  }

  if (!profile || !profile.profile_primary) {
    return (
      <Card data-testid="hcf-profile-card-empty">
        <CardContent className="p-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2 text-foreground font-medium">
            <Sparkles className="w-4 h-4" /> {t('hcf.notAvailable', 'Profilo HCF™ non ancora disponibile')}
          </div>
          <div className="mt-1 text-xs">{t('hcf.notAvailableHint', 'Il lead deve completare il Form 2 (Path + Paese + Budget + Timeline).')}</div>
        </CardContent>
      </Card>
    );
  }

  const country = profile.country || {};
  const path    = profile.path || {};
  const budget  = profile.budget || {};
  const liq     = profile.liquidity || {};
  const call    = profile.call_booking || {};

  const callSignalCls = SIGNAL_COLORS[call.signal] || 'bg-gray-100 text-gray-700';

  return (
    <Card className="border-2 border-[hsl(var(--primary))]/30" data-testid="hcf-profile-card">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold text-[hsl(var(--primary))]">🎴 {t('hcf.cardTitle', 'Scheda Cliente HCF™')}</span>
              {computedInline && (
                <Badge variant="outline" className="text-[10px] font-normal" data-testid="hcf-inline-badge">
                  {t('hcf.livePreview', 'live preview (non salvato)')}
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Pre-Profilazione automatica · Phase 0 (sezioni 1-5)
            </div>
          </div>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={recompute}
              disabled={recomputing}
              data-testid="hcf-recompute-button"
            >
              <RefreshCcw className={`w-3.5 h-3.5 mr-1 ${recomputing ? 'animate-spin' : ''}`} />
              {recomputing ? 'Ricalcolo…' : 'Ricalcola'}
            </Button>
          )}
        </div>

        {/* Archetypes */}
        <div className="flex flex-wrap items-center gap-2">
          <ArchetypePill code={profile.profile_primary} name={profile.profile_primary_name} primary />
          {profile.profile_secondary && (
            <>
              <span className="text-muted-foreground text-xs">+</span>
              <ArchetypePill code={profile.profile_secondary} name={profile.profile_secondary_name} />
            </>
          )}
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${callSignalCls}`}
            data-testid="hcf-call-signal"
          >
            {call.label || '—'}
          </span>
        </div>

        {profile.profile_primary_driver && (
          <div className="text-xs text-muted-foreground italic" data-testid="hcf-primary-driver">
            {t('hcf.mainDriver', 'Driver principale:')} <span className="font-medium not-italic text-foreground">{profile.profile_primary_driver}</span>
          </div>
        )}

        {/* 5 sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 pt-2 border-t">
          <SectionRow
            icon={Globe}
            label={`1 · ${t('hcf.country', 'Paese')}`}
            value={(country.primary || '—').replace(/^./, (c) => c.toUpperCase())}
            hint={country.lever ? `🎯 ${t('hcf.lever', 'Leva')}: ${country.lever}` : null}
            accent="text-emerald-700"
          />
          <SectionRow
            icon={Briefcase}
            label={`2 · ${t('hcf.interest', 'Interesse')}`}
            value={(path.value || '—').replace('_', ' ')}
            hint={path.language ? `🗣 ${path.language}` : null}
          />
          <SectionRow
            icon={Wallet}
            label="3 · Budget"
            value={`${budget.label || '—'}${budget.meaning ? ` · ${budget.meaning}` : ''}`}
            hint={budget.attention ? `⚠️ ${budget.attention}` : null}
            accent="text-amber-700"
          />
          <SectionRow
            icon={Droplet}
            label={`4 · ${t('hcf.liquidity', 'Liquidità (ipotesi)')}`}
            value={liq.label || '—'}
            hint={liq.strategy ? `${t('hcf.strategy', 'Strategia')}: ${liq.strategy}` : null}
          />
          <SectionRow
            icon={CalendarCheck2}
            label="5 · Call booking"
            value={call.label || '—'}
            hint={call.strategy ? `${t('hcf.strategy', 'Strategia')}: ${call.strategy}` : null}
          />
          {country.fear && (
            <SectionRow
              icon={Sparkles}
              label={t('hcf.typicalFear', 'Paura tipica')}
              value={country.fear}
              hint={t('hcf.fearHint', 'Gestire proattivamente nella call')}
              accent="text-rose-700"
            />
          )}
        </div>

        {profile.summary && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="hcf-summary">
            <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold mb-1">{t('hcf.summary', 'Sintesi')}</div>
            <div className="text-sm text-amber-900">
              {renderSafeRichText(profile.summary)}
            </div>
          </div>
        )}

        <div className="text-[10px] text-muted-foreground italic">
          {t('hcf.phase2Note', 'Sezioni 6-9 (prime domande, vocabolario, output finale Associate) sono compilate manualmente dopo la prima call — Phase 2.')}
        </div>
      </CardContent>
    </Card>
  );
}
